import { useState, useEffect, useContext, memo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useReactFlow } from "reactflow";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormHelperText from "@mui/material/FormHelperText";

import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftBox from "components/SoftBox";
import SoftSelect from "components/SoftSelect";

import {
  setIsNotesDialogOpen,
  setNodeToAddNotes,
} from "redux/actions/flowDesigner/flowsActionCreator";
import { TabContextApi } from "layouts/Flow Designer/context";

import { toast } from "react-toastify";

import DiscardDialog from "cxfcomponents/DiscardDialog";

const NOTES_BACKGROUND_COLORS_OPTIONS = [
  { label: "Default", value: "" },
  { label: "Green", value: "green" },
  { label: "Red", value: "red" },
  { label: "Yellow", value: "yellow" },
];

const MAX_CHARACTERS = 500;

const NotesDialog = () => {
  const { isNotesDialogOpen, nodeToAddNotes } = useSelector((store) => store?.designer?.flows);
  const { autoSaveFlow } = useContext(TabContextApi);
  const [notes, setNotes] = useState("");
  const [type, setType] = useState("");
  const [originalNotes, setOriginalNotes] = useState("");
  const [isNotesEdited, setIsNotesEdited] = useState(false);
  const [notesDialogTitle, setNotesDialogTitle] = useState("");
  const [openDiscardDialog, setOpenDiscardDialog] = useState(false);
  const reactFlowInstance = useReactFlow();
  const dispatch = useDispatch();

  const setNotesDefaultState = () => {
    setNotes("");
    setType("");
    setOriginalNotes("");
    setIsNotesEdited(false);
    dispatch(setNodeToAddNotes(null));
  };

  const handleNotesDialogClose = () => {
    if (isNotesEdited) {
      setOpenDiscardDialog(true);
      return;
    }

    setNotesDefaultState();
    dispatch(setIsNotesDialogOpen(false));
  };

  const handleSaveNotes = () => {
    const node = reactFlowInstance.getNode(nodeToAddNotes.id);
    node.data.notes = {
      content: notes,
      type: type,
    };

    autoSaveFlow();

    toast.success("Notes saved successfully");
    setNotesDefaultState();
    dispatch(setIsNotesDialogOpen(false));
  };

  const handleNotesInput = (e) => {
    const { value } = e.target;

    if (value !== originalNotes) {
      setIsNotesEdited(true);
    } else {
      setIsNotesEdited(false);
    }

    if (value?.length > MAX_CHARACTERS) {
      return;
    }

    setNotes(value);
  };

  const handleNoteBackgroundColorChange = (e) => {
    const { value } = e;
    setType(value);
  };

  const handleDiscardDialogClose = () => {
    setOpenDiscardDialog(false);
  };

  const handleDiscardNotes = () => {
    setNotesDefaultState();
    setOpenDiscardDialog(false);
    dispatch(setIsNotesDialogOpen(false));
  };

  useEffect(() => {
    if (nodeToAddNotes) {
      setNotes(nodeToAddNotes?.notes?.content || "");
      setType(nodeToAddNotes?.notes?.type || "");
      setOriginalNotes(nodeToAddNotes?.notes?.content || "");
      setNotesDialogTitle(nodeToAddNotes?.label || nodeToAddNotes?.customLabel || "");
    }
  }, [nodeToAddNotes]);

  return (
    <>
      <Dialog open={isNotesDialogOpen} fullWidth={true} maxWidth="sm">
        <DialogTitle>{`${notesDialogTitle} Notes`}</DialogTitle>
        <DialogContent>
          <SoftBox mt={2}>
            <SoftBox width="30%" mb={2}>
              <SoftSelect
                isSearchable={false}
                placeholder="Select Type"
                label="Background color"
                value={NOTES_BACKGROUND_COLORS_OPTIONS.find((options) => options.value === type)}
                options={NOTES_BACKGROUND_COLORS_OPTIONS}
                onChange={handleNoteBackgroundColorChange}
                menuPlacement="bottom"
              />
            </SoftBox>
            <SoftInput
              className={`node-notes-input ${type}`}
              multiline
              rows={10}
              value={notes}
              onChange={handleNotesInput}
            />
            <FormHelperText error={false} sx={{ textAlign: "end" }}>
              {`${notes.length}/${MAX_CHARACTERS}`}
            </FormHelperText>
          </SoftBox>
        </DialogContent>
        <DialogActions>
          <SoftButton variant="outlined" color="info" onClick={handleNotesDialogClose}>
            Close
          </SoftButton>
          <SoftButton variant="contained" color="info" onClick={handleSaveNotes}>
            Save
          </SoftButton>
        </DialogActions>
      </Dialog>
      <DiscardDialog
        open={openDiscardDialog}
        handleDialogClose={handleDiscardDialogClose}
        onAccept={handleDiscardNotes}
      />
    </>
  );
};

export default memo(NotesDialog);
